import { Injectable } from '@angular/core';

import { CachableData } from '@app/models/cacheable-data';
import isAfter from 'date-fns/esm/isAfter';
import isBefore from 'date-fns/esm/isBefore';
import addMonths from 'date-fns/esm/addMonths';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class CacheInMemoryService {

  private memoryCache;
  private readonly cachePrefix = 'rise-pdp';

  constructor(private authService: AuthService) {

    function storageMockFunc() {
      const storage = {};

      return {
        setItem: function(key, value) {
          storage[key] = value || '';
        },
        getItem: function(key) {
          return key in storage ? storage[key] : null;
        },
        removeItem: function(key) {
          delete storage[key];
        },
        clear: function() {
          for (const key in storage) {
              if (storage.hasOwnProperty(key)) {
                  delete storage[key];
              }
          }
        },
        get length() {
          return Object.keys(storage).length;
        },
        key: function(i) {
          const keys = Object.keys(storage);
          return keys[i] || null;
        }
      };
    }

    (<any>window).storageMock = storageMockFunc();
    this.memoryCache = (<any>window).storageMock;

  }

  private generateCacheKey(cacheKey: string) {
    if (this.authService.accountInfo) {
      if (this.authService.accountInfo.username) {
        return `${this.cachePrefix}-${this.authService.accountInfo.username}-${cacheKey}`;
      }
    }
    return null;
  }

  public get<T>(cacheKey: string): T {
    try {
      const generatedKey = this.generateCacheKey(cacheKey);
      if (generatedKey) {
        const cachedItem = this.memoryCache.getItem(generatedKey);
        if (cachedItem) {
          const cachableData = JSON.parse(cachedItem) as CachableData<T>;
          if (cachableData.AbsoluteExpirationDate) {
            const expirationDate = new Date(cachableData.AbsoluteExpirationDate);
            if (isAfter(expirationDate, new Date())) {
              return cachableData.Data;
            } else {
              this.removeFromMemoryCache(cacheKey);
              return null;
            }
          }
          return cachableData.Data;
        }
      }
      return null;
    } catch {
      return null;
    }
  }

  public set<T>(cacheKey: string, value: T, absoluteExpirationDate?: Date) {
    const generatedKey = this.generateCacheKey(cacheKey);

    if (!absoluteExpirationDate) {
      absoluteExpirationDate = addMonths(new Date(), 3);
    }

    if (generatedKey) {
      this.memoryCache.setItem(generatedKey, JSON.stringify(
        new CachableData({
          Data: value,
          AddedToCache: new Date(),
          AbsoluteExpirationDate: absoluteExpirationDate ? absoluteExpirationDate : null
        })));
    }
  }

  public removeFromMemoryCache(cacheKey: string) {
    const generatedKey = this.generateCacheKey(cacheKey);
    if (generatedKey) {
      this.memoryCache.removeItem(generatedKey);
    }
  }

  public removeAllExpiredItemsInMemoryCache() {
    for (let i = 0; i < this.memoryCache.length; i++) {
      const key = this.memoryCache.key(i);
      const cachedItem = JSON.parse(this.memoryCache.getItem(key)) as CachableData<any>;
      if (cachedItem) {
        if (cachedItem.AbsoluteExpirationDate) {
          const expirationDate = new Date(cachedItem.AbsoluteExpirationDate);
          if (isBefore(expirationDate, new Date())) {
            this.removeFromMemoryCache(key);
          }
        }
      }
    }
  }

  public removeAllitemsInMemoryCacheWithPrefix(cacheKey: string) {
    const generatedKey = this.generateCacheKey(cacheKey);
    if (generatedKey) {
      const tmpArr = []; // Array to hold the keys
      // Iterate over localStorage and insert the keys that meet the condition into arr
      for (let i = 0; i < this.memoryCache.length; i++) {
        if (this.memoryCache.key(i).indexOf(generatedKey) !== -1) {
          tmpArr.push(this.memoryCache.key(i));
        }
      }
      // Iterate over arr and remove the items by key
      for (let i = 0; i < tmpArr.length; i++) {
        this.memoryCache.removeItem(tmpArr[i]);
      }
    }
  }

  public clearMemoryCache() {
    this.memoryCache.clear();
  }

}
